import type { Job, Issue, CustomerCredit } from "./types";

export type JobHealthStatus = "Healthy" | "At Risk" | "Problem";

export interface JobHealth {
  status: JobHealthStatus;
  factors: string[];
}

export function getJobHealth(
  job: Job,
  openIssues: Issue[],
  credits: CustomerCredit[] = []
): JobHealth {
  const factors: string[] = [];

  // Escalated issues
  const escalatedCount = openIssues.filter((i) => i.severity === "Urgent" && isOlderThan3Days(i.dateCreated)).length;
  const urgentCount = openIssues.filter((i) => i.severity === "Urgent").length;
  const totalOpen = openIssues.length;

  if (escalatedCount > 0) factors.push(`${escalatedCount} escalated issue${escalatedCount > 1 ? "s" : ""}`);
  else if (urgentCount > 0) factors.push(`${urgentCount} urgent issue${urgentCount > 1 ? "s" : ""}`);
  else if (totalOpen > 2) factors.push(`${totalOpen} open issues`);

  // Scheduling conflicts / delays
  if (job.scheduledInstallDate && isPastDate(job.scheduledInstallDate) && job.currentStage !== "Completed") {
    factors.push("Past scheduled date");
  }
  if (job.targetInstallDate && isPastDate(job.targetInstallDate) && job.currentStage !== "Completed") {
    factors.push("Past target date");
  }

  // Contractor rejection
  if (job.dispatchStatus === "Rejected") {
    factors.push("Contractor rejected");
  }

  // Credits issued
  const jobCredits = credits.filter((c) => c.jobId === job.id);
  if (jobCredits.length > 0) {
    const total = jobCredits.reduce((s, c) => s + c.creditAmount, 0);
    factors.push(`$${total.toLocaleString()} in credits`);
  }

  // Determine status
  let status: JobHealthStatus;
  const hasCritical = escalatedCount > 0 || (urgentCount > 0 && totalOpen > 1) || job.dispatchStatus === "Rejected";
  const hasWarning = factors.length > 0;

  if (hasCritical) status = "Problem";
  else if (hasWarning) status = "At Risk";
  else status = "Healthy";

  return { status, factors };
}

function isOlderThan3Days(dateStr: string): boolean {
  const d = new Date(dateStr);
  const now = new Date("2026-03-26");
  return (now.getTime() - d.getTime()) / (1000 * 60 * 60 * 24) > 3;
}

function isPastDate(dateStr: string): boolean {
  return new Date(dateStr) < new Date("2026-03-26");
}
